/**
 * VennDiagram.tsx - Interactive Venn Diagram Component
 * 
 * CHANGE HISTORY:
 * - 251026_0950 danielbeer: Created Venn Diagram with plain-language tooltips
 * 
 * Purpose: Visualize overlaps and relationships between packages/features
 * Accessibility: Designed for non-technical users
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

/**
 * Data structure for Venn Diagram
 */
export interface VennSet {
  id: string;
  label: string;
  size: number;
  color?: string;
  description?: string;
}

export interface VennIntersection {
  sets: string[]; // IDs of intersecting sets
  size: number;
  description?: string;
}

export interface VennDiagramProps {
  sets: VennSet[];
  intersections?: VennIntersection[];
  width?: number;
  height?: number;
  showLabels?: boolean;
  showTooltips?: boolean;
  onSetClick?: (set: VennSet) => void;
  onIntersectionClick?: (intersection: VennIntersection) => void;
}

/**
 * VennDiagram Component
 * 
 * Features:
 * - Interactive circles with hover effects
 * - Plain-language tooltips
 * - Click handlers for sets and intersections
 * - Responsive sizing
 * - Accessible color scheme
 */
export const VennDiagram: React.FC<VennDiagramProps> = ({
  sets,
  intersections = [],
  width = 600,
  height = 400,
  showLabels = true,
  showTooltips = true,
  onSetClick,
  onIntersectionClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    content: '',
  });

  useEffect(() => {
    if (!svgRef.current || sets.length === 0) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);
    const centerX = width / 2;
    const centerY = height / 2;

    // Calculate circle positions based on number of sets
    const positions = calculateCirclePositions(sets.length, centerX, centerY, Math.min(width, height) / 3);

    // Create group for circles
    const circlesGroup = svg.append('g').attr('class', 'circles');

    // Draw circles
    sets.forEach((set, index) => {
      const pos = positions[index];
      const radius = Math.sqrt(set.size) * 50; // Scale radius based on size

      const circle = circlesGroup
        .append('circle')
        .attr('cx', pos.x)
        .attr('cy', pos.y)
        .attr('r', radius)
        .attr('fill', set.color || d3.schemeCategory10[index % 10])
        .attr('opacity', 0.5)
        .attr('stroke', '#333')
        .attr('stroke-width', 2)
        .style('cursor', onSetClick ? 'pointer' : 'default')
        .on('mouseenter', function (event) {
          if (showTooltips) {
            d3.select(this).attr('opacity', 0.7);
            
            const plainLanguage = `
              <strong>${set.label}</strong><br/>
              ${set.description || `This represents ${set.label}`}<br/>
              <em>Size: ${set.size} items</em>
            `;
            
            setTooltip({
              visible: true,
              x: event.pageX + 10,
              y: event.pageY - 10,
              content: plainLanguage,
            });
          }
        })
        .on('mouseleave', function () {
          d3.select(this).attr('opacity', 0.5);
          setTooltip({ visible: false, x: 0, y: 0, content: '' });
        })
        .on('click', () => {
          if (onSetClick) onSetClick(set);
        });

      // Add labels
      if (showLabels) {
        circlesGroup
          .append('text')
          .attr('x', pos.x)
          .attr('y', pos.y)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('fill', '#000')
          .attr('font-size', '14px')
          .attr('font-weight', 'bold')
          .attr('pointer-events', 'none')
          .text(set.label);
      }
    });

    // Draw intersection labels if provided
    if (intersections.length > 0 && showLabels) {
      intersections.forEach((intersection) => {
        if (intersection.sets.length === 2) {
          // Find the two sets
          const set1Index = sets.findIndex((s) => s.id === intersection.sets[0]);
          const set2Index = sets.findIndex((s) => s.id === intersection.sets[1]);
          
          if (set1Index !== -1 && set2Index !== -1) {
            const pos1 = positions[set1Index];
            const pos2 = positions[set2Index];
            
            // Calculate midpoint
            const midX = (pos1.x + pos2.x) / 2;
            const midY = (pos1.y + pos2.y) / 2;
            
            // Add intersection marker
            circlesGroup
              .append('circle')
              .attr('cx', midX)
              .attr('cy', midY)
              .attr('r', 8)
              .attr('fill', '#ff6b6b')
              .attr('opacity', 0.8)
              .style('cursor', onIntersectionClick ? 'pointer' : 'default')
              .on('mouseenter', function (event) {
                if (showTooltips) {
                  d3.select(this).attr('opacity', 1);
                  
                  const plainLanguage = `
                    <strong>Overlap</strong><br/>
                    ${intersection.description || `These packages share ${intersection.size} common features`}<br/>
                    <em>${intersection.size} shared items</em>
                  `;
                  
                  setTooltip({
                    visible: true,
                    x: event.pageX + 10,
                    y: event.pageY - 10,
                    content: plainLanguage,
                  });
                }
              })
              .on('mouseleave', function () {
                d3.select(this).attr('opacity', 0.8);
                setTooltip({ visible: false, x: 0, y: 0, content: '' });
              })
              .on('click', () => {
                if (onIntersectionClick) onIntersectionClick(intersection);
              });
          }
        }
      });
    }

  }, [sets, intersections, width, height, showLabels, showTooltips, onSetClick, onIntersectionClick]);

  return (
    <div style={{ position: 'relative', width, height }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          background: '#fafafa',
        }}
      />
      
      {/* Tooltip */}
      {tooltip.visible && showTooltips && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            lineHeight: '1.5',
            maxWidth: '300px',
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
      
      {/* Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          background: 'white',
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid #ddd',
          fontSize: '12px',
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Legend</div>
        {sets.map((set, index) => (
          <div key={set.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: set.color || d3.schemeCategory10[index % 10],
                marginRight: '8px',
                opacity: 0.7,
              }}
            />
            <span>{set.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Calculate circle positions based on number of sets
 * For 2 sets: side by side
 * For 3 sets: triangle
 * For 4+ sets: circular arrangement
 */
function calculateCirclePositions(
  count: number,
  centerX: number,
  centerY: number,
  radius: number
): Array<{ x: number; y: number }> {
  const positions: Array<{ x: number; y: number }> = [];

  if (count === 1) {
    positions.push({ x: centerX, y: centerY });
  } else if (count === 2) {
    positions.push(
      { x: centerX - radius / 2, y: centerY },
      { x: centerX + radius / 2, y: centerY }
    );
  } else if (count === 3) {
    const angle = (2 * Math.PI) / 3;
    for (let i = 0; i < 3; i++) {
      positions.push({
        x: centerX + radius * Math.cos(i * angle - Math.PI / 2),
        y: centerY + radius * Math.sin(i * angle - Math.PI / 2),
      });
    }
  } else {
    // Circular arrangement for 4+ sets
    const angle = (2 * Math.PI) / count;
    for (let i = 0; i < count; i++) {
      positions.push({
        x: centerX + radius * Math.cos(i * angle - Math.PI / 2),
        y: centerY + radius * Math.sin(i * angle - Math.PI / 2),
      });
    }
  }

  return positions;
}

export default VennDiagram;
